import { useGetList } from "ra-core";
import { useMemo } from "react";

import { osloDateKey } from "../misc/osloDate";
import { workdaysBetween } from "../misc/workdays";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CompanyNote, Deal } from "../types";

// Leads i Lead inn som teamet må se i «Å gjøre» (kap. 6.2 og 6.6, A4),
// uansett Mine eller Team:
// - skjemaet kunne ikke tolkes (intake_flag = parse_failed),
// - uten eier i over én virkedag,
// - uten logget kontakt i over én virkedag.

// Virkedager en lead kan stå før den vises for alle.
export const LEAD_GRACE_WORKDAYS = 1;

const NO_DEALS: Deal[] = [];
const NO_NOTES: CompanyNote[] = [];

// Leaden har ventet lenger enn fristen (virkedager etter opprettelsen).
export const leadWaitedTooLong = (
  deal: Pick<Deal, "created_at">,
  today: string = osloDateKey(),
) =>
  !!deal.created_at &&
  workdaysBetween(osloDateKey(new Date(deal.created_at)), today) >
    LEAD_GRACE_WORKDAYS;

// En logget kontakt (kontaktlogg) på leaden eller kunden etter at leaden kom
// inn.
export const hasLoggedContact = (
  deal: Pick<Deal, "id" | "company_id" | "created_at">,
  notes: Pick<
    CompanyNote,
    "deal_id" | "company_id" | "contact_type" | "contacted_at" | "created_at"
  >[],
) =>
  notes.some(
    (n) =>
      !!n.contact_type &&
      ((n.deal_id != null && String(n.deal_id) === String(deal.id)) ||
        (n.deal_id == null &&
          deal.company_id != null &&
          String(n.company_id) === String(deal.company_id) &&
          (n.contacted_at ?? n.created_at ?? "") >= (deal.created_at ?? ""))),
  );

export const useLeadAttention = (enabled: boolean) => {
  const { dealStages } = useConfigurationContext();
  const today = osloDateKey();
  const inboxValues = dealStages.filter((s) => s.is_inbox).map((s) => s.value);
  const { data: leadsRaw = NO_DEALS, isPending: leadsPending } =
    useGetList<Deal>(
      "deals",
      {
        filter: {
          "archived_at@is": null,
          status: "open",
          "stage@in": `(${inboxValues.join(",")})`,
        },
        sort: { field: "created_at", order: "ASC" },
        pagination: { page: 1, perPage: 200 },
      },
      { enabled: enabled && inboxValues.length > 0 },
    );
  // Bufrede lister kan ha fått en endret post (eier satt, flyttet ut av
  // Lead inn): sjekk på nytt.
  const leads = useMemo(
    () =>
      leadsRaw.filter(
        (d) =>
          !d.archived_at &&
          d.status === "open" &&
          !!d.stage &&
          inboxValues.includes(d.stage),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leadsRaw, inboxValues.join(",")],
  );
  const waiting = leads.filter((d) => leadWaitedTooLong(d, today));
  const companyIds = [
    ...new Set(
      waiting
        .filter((d) => d.sales_id != null && d.company_id != null)
        .map((d) => String(d.company_id)),
    ),
  ];
  const { data: notes = NO_NOTES, isPending: notesPending } =
    useGetList<CompanyNote>(
      "company_notes",
      {
        filter: {
          "company_id@in": `(${companyIds.join(",")})`,
          "contact_type@not.is": null,
        },
        sort: { field: "created_at", order: "DESC" },
        pagination: { page: 1, perPage: 500 },
      },
      { enabled: enabled && companyIds.length > 0 },
    );

  return {
    parseFailed: leads.filter((d) => d.intake_flag === "parse_failed"),
    unowned: waiting.filter((d) => d.sales_id == null),
    noContact: waiting.filter(
      (d) => d.sales_id != null && !hasLoggedContact(d, notes),
    ),
    isPending:
      enabled &&
      inboxValues.length > 0 &&
      (leadsPending || (companyIds.length > 0 && notesPending)),
  };
};
